import React, { useState, useEffect, ChangeEvent } from 'react';
import CompanyShippingLocationsSelection from '@/components/CompanyShippingLocationsSelection';
import { IOSSwitch } from '@/components/IOSSwitch';
import ValidationCard from '@/components/ItemPriceWindow/ValidationCard';
import ModalDefault from '@/components/Modals/ModalDefault';
import MicroCurrencyInput from '@/components/NumberFormat/MicrosInput/microCurrencyInput';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import { ILocation } from '@/types/api/locations';
import {
  generateTimeOptions,
  getUpdatedInputNumberOfDays,
} from '@/utils/components/configureShippingData';
import { ONE_DAY_IN_SECONDS } from '@/utils/dateUtils';
import { handleErrorAsync } from '@/utils/handlers';
import { Close } from '@mui/icons-material';
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  Box,
  Modal,
  useMediaQuery,
  useTheme,
} from '@mui/material';

const DEFINE_OPTION_LABEL = 'Definir';
const DEFINE_OPTION_ID = 1;

interface ConfigureShippingDataProps {
  isOpen: boolean;
  onSuccess: () => void;
  handleClose: () => void;
}

interface IPayloadData {
  base_freight_micros: number;
  min_price_for_free_shipping_micros: number | null;
  shipping_locations: number[];
  base_arrival_estimate_seconds: number;
  full_name: string | undefined;
  has_confirmed_shipping: boolean;
  has_autofill_enabled: boolean;
}

function ConfigureShippingData(props: ConfigureShippingDataProps) {
  const { isOpen, onSuccess, handleClose } = props;
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { setIsLoading } = useLoading();
  const { updateUserData, defaultMembership, user } = useUser();
  const defaultCompany = defaultMembership?.company;

  const baseArrivalInDays = Math.floor(
    (defaultCompany?.base_arrival_estimate_seconds || 0) / ONE_DAY_IN_SECONDS
  );
  const secondsToDays = defaultCompany?.base_arrival_estimate_seconds
    ? getUpdatedInputNumberOfDays(baseArrivalInDays)
    : 'Definir';

  const [minPriceForFreeShippingMicros, setMinPriceForFreeShippingMicros] =
    useState(defaultCompany?.min_price_for_free_shipping_micros || null);
  const [baseFreightMicros, setBaseFreightMicros] = useState(
    Number(defaultCompany?.base_freight_micros) || null
  );

  const [isFreeFreightForAnyValue, setIsFreeFreightForAnyValue] = useState(
    !defaultCompany?.base_freight_micros
  );

  const [locationsChecked, setLocationsChecked] = useState<ILocation[]>([]);
  const [optionsTimePrice, setOptionsTimePrice] = useState(
    generateTimeOptions(secondsToDays)
  );
  const [isMinimumValueFreeFreight, setIsMinimumValueFreeFreight] = useState(
    Boolean(defaultCompany?.min_price_for_free_shipping_micros)
  );
  const [isOpenModalDefineExpiration, setIsOpenModalDefineExpiration] =
    useState(false);
  const [numberOfDays, setNumberOfDays] = useState(baseArrivalInDays);
  const optionsValue = optionsTimePrice.map(option => option.value);
  const [inputNumberOfDays, setInputNumberOfDays] = useState(
    !optionsValue.includes(baseArrivalInDays) ? baseArrivalInDays : 0
  );
  const [isActiveAutoFill, setIsActiveAutoFill] = useState(
    !!defaultCompany?.has_autofill_enabled
  );

  const handleFreightChange = (microValue: number) => {
    if (microValue < 0)
      return Toast('error', 'Números negativos não são válidos!');

    setBaseFreightMicros(microValue);
    if (microValue === 0) {
      setIsFreeFreightForAnyValue(true);
      setMinPriceForFreeShippingMicros(null);
      return;
    }

    setIsFreeFreightForAnyValue(false);
  };

  const handleDaysChange = (event: ChangeEvent<HTMLInputElement>) => {
    const days = Number(event.target.value);
    if (days < 0 || days > Number.MAX_SAFE_INTEGER) return;

    setInputNumberOfDays(days);
  };

  const handleAddCustomDate = () => {
    if (!inputNumberOfDays) return Toast('error', 'Selecione um valor de dias');

    setNumberOfDays(inputNumberOfDays);

    setOptionsTimePrice(prevOptions =>
      prevOptions.map(option => {
        const isDefineOption = option.id === DEFINE_OPTION_ID;
        const optionValueExists = optionsValue.includes(inputNumberOfDays);

        if (isDefineOption) {
          return {
            ...option,
            name: optionValueExists
              ? DEFINE_OPTION_LABEL
              : getUpdatedInputNumberOfDays(inputNumberOfDays),
            selected: !optionValueExists,
          };
        }

        return {
          ...option,
          selected: optionValueExists && inputNumberOfDays === option.value,
        };
      })
    );

    setIsOpenModalDefineExpiration(false);
  };

  const handleCloseCustomDate = () => {
    setIsOpenModalDefineExpiration(false);
    const optionValueExists = optionsValue.includes(numberOfDays);

    setOptionsTimePrice(prevOptions =>
      prevOptions.map(option => ({
        ...option,
        selected: optionValueExists
          ? numberOfDays === option.value
          : option.id === DEFINE_OPTION_ID && !!numberOfDays,
      }))
    );
  };

  const handleFreeFreightChange = (microValue: number) => {
    if (microValue < 0) {
      Toast('error', 'Números negativos não são válidos!');
      return;
    }

    if (microValue === 0) {
      setMinPriceForFreeShippingMicros(null);
      setIsMinimumValueFreeFreight(false);
      return;
    }

    setMinPriceForFreeShippingMicros(microValue);
  };

  const generatePayloadData = () => {
    const baseArrivalSeconds = numberOfDays * ONE_DAY_IN_SECONDS;

    const payload: IPayloadData = {
      base_freight_micros: Number(baseFreightMicros),
      min_price_for_free_shipping_micros: minPriceForFreeShippingMicros,
      shipping_locations: locationsChecked.map(({ id }) => id),
      full_name: defaultCompany?.full_name,
      has_confirmed_shipping: true,
      base_arrival_estimate_seconds: baseArrivalSeconds,
      has_autofill_enabled: isActiveAutoFill,
    };

    return payload;
  };

  const handleActiveMinimumValueFreeFreight = () => {
    setIsMinimumValueFreeFreight(prevValue => {
      if (prevValue) setMinPriceForFreeShippingMicros(null);

      return !prevValue;
    });
  };

  const handleSelectExpireOption = (selectedId: number) => {
    setOptionsTimePrice(prevOptions =>
      prevOptions.map(currentOption => {
        const isDefineOption = currentOption.id === DEFINE_OPTION_ID;
        const hasSelectedDefineOption = selectedId !== DEFINE_OPTION_ID;

        if (currentOption.id === selectedId && hasSelectedDefineOption) {
          setNumberOfDays(currentOption.value as number);
        }

        return {
          ...currentOption,
          selected: currentOption.id === selectedId,
          name:
            isDefineOption && hasSelectedDefineOption
              ? DEFINE_OPTION_LABEL
              : currentOption.name,
        };
      })
    );

    setIsOpenModalDefineExpiration(selectedId === DEFINE_OPTION_ID);
  };

  const handleToggleFreeFreightForAnyValue = () => {
    setIsFreeFreightForAnyValue(prevChecked => {
      if (!prevChecked) {
        setMinPriceForFreeShippingMicros(null);
        setBaseFreightMicros(0);
        setIsMinimumValueFreeFreight(false);
      }
      return !prevChecked;
    });
  };

  const handleUpdateSuccess = () => {
    handleClose();
    onSuccess();
  };

  const handleSubmitCompanyShippingData = async () => {
    if (!isFreeFreightForAnyValue && !baseFreightMicros)
      return Toast(
        'error',
        'Defina um valor de frete padrão ou marque como frete grátis.'
      );

    if (isMinimumValueFreeFreight && !minPriceForFreeShippingMicros)
      return Toast(
        'error',
        'Valor mínimo para frete grátis deve ser maior que R$ 0,00'
      );

    if (optionsTimePrice.every(selectOption => !selectOption.selected))
      return Toast('error', 'Defina um prazo médio de entrega.');

    if (!locationsChecked.length)
      return Toast('error', 'Selecione um local de entrega para prosseguir.');

    setIsLoading(true);
    const [companyUpdated, error] = await handleErrorAsync(async () => {
      const payload = generatePayloadData();
      const { data } = await api.patch<ICompany>(
        `/api/companies/${defaultMembership?.company.id}/`,
        payload
      );

      return data;
    }).finally(() => setIsLoading(false));

    if (error) {
      Toast('error', error.message);
      return;
    }

    updateUserData({
      memberships: user.memberships?.map(membership =>
        membership.company.id === defaultCompany?.id
          ? { ...membership, company: companyUpdated }
          : membership
      ),
    });

    Toast('success', 'Dados de entrega e pagamento atualizados com sucesso!');
    handleUpdateSuccess();
  };

  function handleLocationsInputChange(updatedLocations: ILocation[]) {
    setLocationsChecked(updatedLocations);
  }

  useEffect(() => {
    if (!defaultMembership) return;

    const fetchData = async () => {
      setIsLoading(true);
      const [company, error] = await handleErrorAsync(async () => {
        const { data } = await api.get<ICompany>(
          `/api/companies/${defaultCompany?.id}/`
        );

        return data;
      }).finally(() => setIsLoading(false));

      if (error) {
        Toast('error', error.message || 'Erro ao buscar dados da empresa');
        return;
      }

      setLocationsChecked(company.shipping_locations);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMembership]);

  return (
    <Modal
      aria-labelledby="modal-configure-shipping-data"
      aria-describedby="modal-configure-shipping-data-description"
      open={isOpen}
      sx={{ display: 'grid', placeItems: 'center' }}
    >
      <>
        <MainContainer
          size="lg"
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundColor: 'background.paper',
            padding: 0,
            overflow: 'hidden',
            [theme.breakpoints.up('md')]: {
              height: '90vh',
              borderRadius: '28px',
            },
          }}
        >
          <Box
            sx={{
              boxShadow: '0px 1px 1px 0px #0000001A',
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '18px 16px',
            }}
          >
            <IconButton
              data-testid="close-button"
              onClick={handleClose}
              sx={{
                color: '#000000',
                position: 'absolute',
                right: mdUp ? '2%' : '5%',
              }}
            >
              <Close />
            </IconButton>

            <Typography variant="h6" component="h2">
              Entrega e pagamento
            </Typography>
          </Box>

          <Box sx={{ height: 'calc(100% - 68px)', overflow: 'auto', pb: 4 }}>
            <MainContainer
              sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '353px',
                  gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Frete padrão
                </Typography>

                <Box>
                  <Typography variant="body2">Valor de frete padrão</Typography>

                  <Typography variant="caption" color="text.secondary">
                    Defina um valor de frete padrão para todas as compras.
                  </Typography>
                </Box>

                <MicroCurrencyInput
                  onChangeMicroValue={handleFreightChange}
                  value={baseFreightMicros}
                  variant="standard"
                  data-testid="base-freight-input"
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    margin: '8px 0 0 -10px',
                  }}
                >
                  <IOSSwitch
                    checked={isFreeFreightForAnyValue}
                    onChange={handleToggleFreeFreightForAnyValue}
                    data-testid="free-freight-any-value-checkbox"
                  />

                  <Typography variant="caption">
                    Frete grátis para qualquer valor de compra
                  </Typography>
                </Box>
              </Box>

              {Boolean(baseFreightMicros) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    Frete promocional
                  </Typography>

                  <Box>
                    <Typography variant="body2">
                      Deseja atribuir frete grátis de acordo com um valor mínimo
                      de compra?
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Ex. Frete grátis acima de R$500,00 na compra.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      margin: '8px 0 0 -10px',
                      gap: 2,
                    }}
                  >
                    <IOSSwitch
                      checked={isMinimumValueFreeFreight}
                      onChange={handleActiveMinimumValueFreeFreight}
                      data-testid="promotional-freight-checkbox"
                    />

                    <Typography variant="caption">Frete promocional</Typography>
                  </Box>
                </Box>
              )}

              {isMinimumValueFreeFreight && (
                <Box sx={{ my: 1, maxWidth: '353px' }}>
                  <Typography variant="body2">
                    Valor mínimo para frete grátis:
                  </Typography>

                  <MicroCurrencyInput
                    onChangeMicroValue={handleFreeFreightChange}
                    value={minPriceForFreeShippingMicros || 0}
                    data-testid="promotional-freight-input"
                    variant="standard"
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ color: 'text.secondary' }}>
                  <Typography component="h3" variant="h6">
                    Entrega padrão
                  </Typography>

                  <Typography variant="body2">
                    Defina um prazo médio de entrega
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {optionsTimePrice.map(({ id, name, selected }) => (
                    <ValidationCard
                      key={id}
                      textCard={name}
                      selected={selected}
                      setSelectedTimePrice={() => handleSelectExpireOption(id)}
                    />
                  ))}
                </Grid>
              </Box>

              <CompanyShippingLocationsSelection
                selectedLocations={locationsChecked}
                handleChangeLocations={handleLocationsInputChange}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Preferências comerciais
                </Typography>

                <Box>
                  <Typography variant="body2">
                    Ativar resposta automática com o preço de seus itens da
                    vitrine?
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Com a resposta automática você economiza tempo para
                    responder suas cotações.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    margin: '8px 0 0 -10px',
                  }}
                >
                  <IOSSwitch
                    checked={isActiveAutoFill}
                    onChange={() =>
                      setIsActiveAutoFill(prevActive => !prevActive)
                    }
                    data-testid="autofill-checkbox"
                  />

                  <Typography variant="caption">Resposta automática</Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handleSubmitCompanyShippingData}
                sx={{ width: mdUp ? 382 : '100%', margin: '0 auto' }}
              >
                Salvar
              </Button>
            </MainContainer>
          </Box>
        </MainContainer>

        <ModalDefault
          title="Definir data"
          isOpen={isOpenModalDefineExpiration}
          onClose={handleCloseCustomDate}
          onClickButton={handleAddCustomDate}
          textButton="Adicionar"
        >
          <Box>
            <Typography variant="body2">Prazo de entrega (em dias)</Typography>

            <TextField
              type="number"
              value={inputNumberOfDays}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={handleDaysChange}
              sx={{ my: 1 }}
              data-testid="define-days-input"
              fullWidth
            />
          </Box>
        </ModalDefault>
      </>
    </Modal>
  );
}

export default ConfigureShippingData;
export type { ConfigureShippingDataProps };
